import type {
  NaamAksharOption,
  NaamConfirmedPerson,
  NaamMatchingResponse,
  NaamPersonReadiness,
  NaamReadinessResponse,
} from "@ishvaram/api-client";
import { apiRequest } from "./_fetch";

export type {
  NaamAksharOption,
  NaamConfirmedPerson,
  NaamMatchingResponse,
  NaamPersonReadiness,
  NaamReadinessResponse,
};

/** What the seeker typed, and how they told us to read it. */
export interface NaamInput {
  value: string;
  /** True = an everyday name (may only SEED the chooser, never resolve it). */
  isName: boolean;
}

/**
 * Ask what these two inputs settle, or what we still have to put to the seeker.
 *
 * Deliberately returns no score. Roman spellings collapse the retroflex/dental
 * pairs Devanagari keeps apart, so a dozen of them name more than one nakshatra
 * — and several of those straddle the traditional 18-guna line. The backend
 * refuses to pick; this call surfaces the question instead.
 */
export async function naamMatchingReadiness(
  personA: NaamInput,
  personB: NaamInput
): Promise<NaamReadinessResponse> {
  return apiRequest<NaamReadinessResponse>("/matching/naam-akshar/readiness", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      person_a_input: personA.value,
      person_b_input: personB.value,
      person_a_is_name: personA.isName,
      person_b_is_name: personB.isName,
    }),
  });
}

/**
 * Score two CONFIRMED (nakshatra, pada) cells.
 *
 * Never send the raw akshar here: a re-sent ambiguous akshar cannot encode which
 * candidate the seeker picked. The server re-looks-up both cells, so an unknown
 * one is a 422 rather than a silently mis-derived rashi.
 */
export async function calculateNaamMatching(
  personA: NaamConfirmedPerson,
  personB: NaamConfirmedPerson,
  language: string
): Promise<NaamMatchingResponse> {
  return apiRequest<NaamMatchingResponse>("/matching/naam-akshar", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ person_a: personA, person_b: personB, language }),
  });
}
